import { useState } from 'react';
import { CirclePlus, Lightbulb, Snowflake } from 'lucide-react';
import { ICEBOX_STATUS, IDEA_STATUS } from './_enums';
import FileItemDialog from './components/FileItemDialog';
import ItemCard from './components/ItemCard';
import ItemGrid from './components/ItemGrid';
import StatusBadge from './components/StatusBadge';
import ViewItemDialog from './components/ViewItemDialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useZealTasks, usePostZealIdea } from '@/data/zealTask';

const IdeaBadge = ({ idea }) => (
  <StatusBadge status={IDEA_STATUS}>
    <Lightbulb className="mr-1 h-3 w-3" />
    <span className="font-mono">{idea.id}</span>
  </StatusBadge>
);

const IceboxBadge = ({ idea }) => (
  <StatusBadge status={ICEBOX_STATUS}>
    <Snowflake className="mr-1 h-3 w-3" />
    <span className="font-mono">{idea.id}</span>
  </StatusBadge>
);

// Admin only views the Icebox: tasks move into and out of it in Zeal.
const VIEWS = {
  [IDEA_STATUS]: {
    label: 'Ideas',
    icon: <Lightbulb />,
    Badge: IdeaBadge,
    description: 'Ideas for the dev team. A developer moves one to To Do when it is picked up.',
    empty: { icon: <Lightbulb />, title: 'No Ideas Yet', description: 'File one with New Idea' }
  },
  [ICEBOX_STATUS]: {
    label: 'Icebox',
    icon: <Snowflake />,
    Badge: IceboxBadge,
    description: 'Ideas set aside in Zeal as future tasks. A developer moves one out of the Icebox when it is picked up.',
    empty: { icon: <Snowflake />, title: 'Icebox is empty', description: 'Ideas set aside in Zeal show up here' }
  }
};

const FeatureIdeas = () => {
  const [status, setStatus] = useState(IDEA_STATUS);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const tasks = useZealTasks();
  const postZealIdea = usePostZealIdea();

  const view = VIEWS[status];
  const ideas = tasks.data
    ? tasks.data.query
        .map((id) => tasks.data.content[id])
        .filter((task) => task.status === status)
        .sort((a, b) => `${b.createdAt}`.localeCompare(`${a.createdAt}`))
    : [];

  return (
    <>
      <ItemGrid
        title="Feature ideas"
        description={view.description}
        action={
          <div className="flex items-center gap-2">
            <Tabs value={status} onValueChange={setStatus}>
              <TabsList>
                {Object.entries(VIEWS).map(([value, { label, icon }]) => (
                  <TabsTrigger key={value} value={value}>
                    {icon}
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button onClick={() => setIsAddOpen(true)}>
              <CirclePlus />
              New Idea
            </Button>
          </div>
        }
        isLoading={tasks.isLoading && !tasks.data}
        error={tasks.error && `An error occurred loading ideas: ${tasks.error.message}`}
        items={ideas}
        empty={view.empty}
        renderItem={(idea) => <ItemCard key={idea.id} item={idea} badge={<view.Badge idea={idea} />} onView={() => setViewing(idea)} />}
      />

      {isAddOpen && (
        <FileItemDialog
          title="New Idea"
          description="Filed in Zeal as a task with status Idea. It cannot be edited from here once filed."
          submitLabel="File Idea"
          onCreate={postZealIdea}
          getTarget={(idea) => idea.id}
          onClose={() => setIsAddOpen(false)}
          onSuccess={() => {
            setStatus(IDEA_STATUS);
            tasks.mutate();
          }}
        />
      )}
      {viewing && (
        <ViewItemDialog item={viewing} badge={viewing.status === ICEBOX_STATUS ? <IceboxBadge idea={viewing} /> : <IdeaBadge idea={viewing} />} onClose={() => setViewing(null)} />
      )}
    </>
  );
};

export default FeatureIdeas;
